/**
 * NB: When creating query keys, it is useful to create "Key Factories" similar to the below
 * This makes referring to queries & their different levels of caching much easier
 * https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 *
 * export const exampleKeys = {
 *   all: ["all"] as const,
 *   lists: () => [...exampleKeys.all, "list"] as const,
 *   list: (filters: string) => [...exampleKeys.lists(), { filters }] as const,
 *   details: () => [...exampleKeys.all, "detail"] as const,
 *   detail: (id: string) => [...exampleKeys.details(), id] as const,
 * };
 */

import type {
  PreviewPurchaseRequestTimelineReq,
  ProductFinishType,
  PurchaseRequestPreviewReq,
} from "adl-gen/ferovinum/app/api";
import type { Currency, StorageLocationId } from "adl-gen/ferovinum/app/db";

export const userKeys = {
  all: ["users"] as const,
  memberships: () => [...userKeys.all, "memberships"] as const,
  nominatedPurchasers: (purchaserId: string) =>
    [...userKeys.memberships(), purchaserId] as const,
};

export const storageLocationKeys = {
  all: ["storage-locations"] as const,
  getStorageLocations: (organisationId: string) =>
    [
      ...storageLocationKeys.all,
      "getStorageLocations",
      organisationId,
    ] as const,
  getStorageLocationProductHoldings: (
    storageLocationId: StorageLocationId,
    searchTerm: string,
  ) =>
    [
      ...storageLocationKeys.all,
      "getStorageLocationProductHoldings",
      storageLocationId,
      searchTerm,
    ] as const,
};

export const productKeys = {
  all: ["products"] as const,
  productSearch: (organisationId: string, searchTerm: string) =>
    [...productKeys.all, "search", organisationId, searchTerm] as const,
  getProducts: (productIds: string[]) =>
    [...productKeys.all, "getProducts", productIds] as const,
  getProductIds: (
    storageLocationId: StorageLocationId | null,
    finishType: ProductFinishType | null,
  ) =>
    [
      ...productKeys.all,
      "getProductIds",
      storageLocationId,
      finishType,
    ] as const,
  availableToSell: () => [...productKeys.all, "availableToSell"] as const,
  availableToSellByProductIds: (
    productIds: string[],
    storageLocationId: string,
  ) =>
    [...productKeys.availableToSell(), storageLocationId, productIds] as const,
  getProductSalePrices: (nominatedPurchaserId: string) =>
    [...productKeys.all, "getProductSalePrices", nominatedPurchaserId] as const,
  getProducible: (organisationId: string) =>
    [...productKeys.all, "getProducible", organisationId] as const,
};

export const nominatedPurchasersKeys = {
  all: ["nominated-purchasers"] as const,
  getNominatedPurchasers: () =>
    [...nominatedPurchasersKeys.all, "getNominatedPurchasers"] as const,
  getNominatedPurchasersByOrg: (organisationId: string) =>
    [
      ...nominatedPurchasersKeys.all,
      "getNominatedPurchasersByOrg",
      organisationId,
    ] as const,
  getNominatedPurchaserIndexItemsByOrg: (organisationId: string) =>
    [
      ...nominatedPurchasersKeys.all,
      "getNominatedPurchaserIndexItemsByOrg",
      organisationId,
    ] as const,
  getNominatedPurchaserDetailsView: (nominatedPurchaserId: string) =>
    [
      ...nominatedPurchasersKeys.all,
      "getNominatedPurchaserDetailsView",
      nominatedPurchaserId,
    ] as const,
  getNominatedPurchaserOutstandingBalanceSummaryPdf: (
    nominatedPurchaseId: string,
  ) =>
    [
      ...nominatedPurchasersKeys.all,
      "getNominatedPurchaserOutstandingBalanceSummaryPdf",
      nominatedPurchaseId,
    ] as const,
  getPurchaserSavedDeliveryInfoList: (nominatedPurchaserId: string) =>
    [
      ...nominatedPurchasersKeys.all,
      "deliveryInfoList",
      nominatedPurchaserId,
    ] as const,
};

export const storefrontKeys = {
  all: ["storefront"] as const,
  configs: (configId: string) =>
    [...storefrontKeys.all, "configs", configId] as const,
  allocations: (configId: string) =>
    [...storefrontKeys.configs(configId), "allocations"] as const,
  saveConfigs: (configId: string) =>
    [...storefrontKeys.configs(configId), "save"] as const,
  toggle: (configId: string) =>
    [...storefrontKeys.configs(configId), "toggle"] as const,
};

export const settlementCreditKeys = {
  all: ["settlement-credit"] as const,
  details: (counterpartyRef: string, currency: Currency) =>
    [
      ...settlementCreditKeys.all,
      "details",
      counterpartyRef,
      currency,
    ] as const,
};

export const tradeSalesKeys = {
  all: ["trade-sales"] as const,
  summaryPreview: (req: PurchaseRequestPreviewReq) =>
    [...tradeSalesKeys.all, "summary-preview", req] as const,
  amendmentSummaryPreview: (
    req: PurchaseRequestPreviewReq,
    purchaseRequestId?: string,
  ) =>
    [
      ...tradeSalesKeys.all,
      "amendment-summary-preview",
      req,
      purchaseRequestId,
    ] as const,
  timelinePreview: (req: PreviewPurchaseRequestTimelineReq) =>
    [...settlementCreditKeys.all, "timeline-preview", req] as const,
  savedProductPrices: (organisationId: string, purchaserId: string | null) =>
    [
      ...tradeSalesKeys.all,
      "saved-product-prices",
      organisationId,
      purchaserId,
    ] as const,
  purchaserProductPrices: (organisationId: string, purchaserId: string) =>
    [
      ...tradeSalesKeys.all,
      "purchaser-product-prices",
      organisationId,
      purchaserId,
    ] as const,
  saleProducts: (productIds: string[]) =>
    [...productKeys.all, "sale-products", productIds] as const,
  storageLocationSupportsDelivery: (storageLocationId: string) =>
    [
      ...tradeSalesKeys.all,
      "storage-location-supports-delivery",
      storageLocationId,
    ] as const,
};

export const organisationKeys = {
  all: ["organisations"] as const,
  getOrganisationDeliveryLocations: (organisationId: string) =>
    [
      ...organisationKeys.all,
      "getOrganisationDeliveryLocations",
      organisationId,
    ] as const,
};

export const physicalStockKeys = {
  all: ["physical-stock"] as const,
  getStock: (
    organisationId: string,
    storageLocationId: string,
    holdId?: string,
    releasedId?: string,
  ) =>
    [
      ...physicalStockKeys.all,
      "getStock",
      organisationId,
      storageLocationId,
      holdId,
      releasedId,
    ] as const,
};

export const physicalAddressKeys = {
  all: ["physicalAddress"] as const,
  getAddress: (entityId: string, addressType: string) =>
    [...physicalAddressKeys.all, "getAddress", entityId, addressType] as const,
};

export const todosKeys = {
  all: ["todos"] as const,
  list: () => [...todosKeys.all, "list"] as const,
};

export const dashboardKeys = {
  all: ["dashboard"] as const,
  analytics: () => [...dashboardKeys.all, "analytics"] as const,
  inventoryData: (organisationId: string, dateRange?: string) =>
    [
      ...dashboardKeys.analytics(),
      "inventory",
      organisationId,
      dateRange,
    ] as const,
  expiringDealsData: (organisationId: string, dateRange?: string) =>
    [
      ...dashboardKeys.analytics(),
      "expiring-deals",
      organisationId,
      dateRange,
    ] as const,
  salesData: (organisationId: string, dateRange?: string) =>
    [...dashboardKeys.analytics(), "sales", organisationId, dateRange] as const,
};
